import { Autocomplete, FormControl, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ChangeEvent } from 'react';
import { useOutletContext } from 'react-router';
import { useAppDataContext } from '../../../../contexts/appData';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import useQueryErrorNotifier from '../../../../hooks/useQueryErrorNotifier';
import { Operator } from '../../../../types/operator';
import { VesselPartialFormProps, VesselTemplateContext } from '../types';
import { useCurrentUser } from '../../../../contexts/currentUser';
import FormLayout, {
  FormLayoutInputElement,
} from '../../../../components/FormLayout';

const VesselBasicDetailsForm = ({ formManager }: VesselPartialFormProps) => {
  const {
    formState,
    materialInputHelpers: { getBaseProps, getTextFieldProps, renderError },
  } = formManager;
  const { values, errors, updateValues, touchField } = formState;
  const { t } = useLanguage();
  const { getOperators } = useIotsApi();
  const { appData } = useAppDataContext();
  const { setDisplayedVesselName, vesselId } =
    useOutletContext<VesselTemplateContext>();
  const { isOperator } = useCurrentUser();

  const {
    data: operators = [],
    isFetching: isFetchingOperators,
    error: operatorsError,
  } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // Default sorting by name
    select: operators =>
      [...operators].sort((a, b) => a.name.localeCompare(b.name)),
  });

  useQueryErrorNotifier([operatorsError]);

  const handleVesselNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateValues({ name: event.target.value });
    setDisplayedVesselName(event.target.value);
  };

  const isOperatorAutocompleteDisabled =
    isFetchingOperators || isOperator || !!vesselId;

  return (
    <FormLayout>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {!isOperator && (
          <FormLayoutInputElement required label={t('content.operator')}>
            <FormControl error={getBaseProps('operatorId').error}>
              <Autocomplete<Operator>
                value={
                  operators.find(({ id }) => id === values.operatorId) || null
                }
                onChange={(_event, selectedOption) =>
                  updateValues({ operatorId: selectedOption?.id ?? '' }, true)
                }
                options={operators}
                onBlur={() => touchField('operatorId')}
                loading={isFetchingOperators}
                disabled={
                  getBaseProps('operatorId').disabled ||
                  isOperatorAutocompleteDisabled
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    error={getBaseProps('operatorId').error}
                    placeholder={t('content.select')}
                  />
                )}
                size="small"
                getOptionLabel={operator =>
                  `${operator.name}${operator.isActive ? '' : ` (${t('content.inactive')})`}`
                }
                getOptionDisabled={option => !option.isActive}
              />
              {renderError('operatorId')}
            </FormControl>
          </FormLayoutInputElement>
        )}
        <FormLayoutInputElement required label={t('content.vessel-name')}>
          <TextField
            placeholder={t('content.vessel-name')}
            {...getTextFieldProps('name')}
            onChange={handleVesselNameChange}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement required label={t('content.flag')}>
          <FormControl error={getBaseProps('flag').error}>
            <Autocomplete<string>
              {...getBaseProps('flag')}
              options={appData.countries}
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('flag').error}
                  placeholder={t('content.select')}
                />
              )}
              getOptionLabel={country =>
                country ? t(`countries.${country}`) : ''
              }
              onChange={(_event, selectedOption) =>
                updateValues({ flag: selectedOption || '' }, true)
              }
            />
            {renderError('flag')}
          </FormControl>
        </FormLayoutInputElement>
        <FormLayoutInputElement required label={t('content.reg-or-imo')}>
          <TextField
            {...getTextFieldProps('regNumber')}
            placeholder={t('content.reg-or-imo')}
            disabled={getBaseProps('regNumber').disabled || !!vesselId}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement required label={t('content.year-of-build')}>
          <FormControl error={Boolean(errors.yearOfBuild)}>
            <DatePicker
              className="w-full"
              name="yearOfBuild"
              disabled={getBaseProps('yearOfBuild').disabled}
              views={['year']}
              format="YYYY"
              maxDate={dayjs(`${dayjs().year()}-12-31`)}
              slotProps={{
                textField: {
                  className: 'py-1.5',
                  error: Boolean(errors.yearOfBuild),
                  onBlur: () => touchField('yearOfBuild'),
                },
              }}
              value={
                values.yearOfBuild ? dayjs(values.yearOfBuild, 'YYYY') : null
              }
              onChange={newValue =>
                updateValues({
                  yearOfBuild: newValue ? newValue.format('YYYY') : '',
                })
              }
            />
            {renderError('yearOfBuild')}
          </FormControl>
        </FormLayoutInputElement>
      </LocalizationProvider>
    </FormLayout>
  );
};

export default VesselBasicDetailsForm;
